// src/pages/DataDeletionPage.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-2xl font-bold text-gray-800 mb-3 border-l-4 border-blue-600 pl-4">{title}</h2>
    <div className="text-gray-600 leading-relaxed space-y-3 pl-4">{children}</div>
  </div>
);

function DataDeletionPage() {
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Opens the user's mail client with a pre-filled deletion request
    const subject = encodeURIComponent('Data Deletion Request – Kalyani Consultancy');
    const body = encodeURIComponent(
      `Hello,\n\nI would like to request the deletion of all personal data associated with my account.\n\nEmail / Facebook-linked account: ${email}\n\nPlease confirm once the data has been removed.\n\nThank you.`
    );
    window.location.href = `mailto:kalyaniconsultancy18@gmail.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <>
      {/* Hero */}
      <section className="bg-sky text-ink py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            className="text-3xl sm:text-5xl font-extrabold mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Data Deletion Request
          </motion.h1>
          <motion.p
            className="text-slate-500 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Your privacy matters to us. You can request deletion of your data at any time.
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <p className="text-gray-600 mb-10 leading-relaxed">
            If you have used <strong>Facebook / Meta Login</strong> to access any feature on the{' '}
            <strong>Kalyani Consultancy</strong> website, or if you have interacted with us via
            Meta platforms (Facebook, Instagram, WhatsApp), we may hold limited personal data
            associated with that interaction. This page explains what data we collect and how you
            can request its deletion.
          </p>

          <Section title="1. What Data We Collect via Meta">
            <p>When you log in or interact with us through Meta platforms, we may receive:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Your name and profile picture (public profile).</li>
              <li>Your email address (if granted permission).</li>
              <li>Your Meta user ID for authentication purposes.</li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> store your Facebook password or access your friends list,
              posts, or other private data.
            </p>
          </Section>

          <Section title="2. How to Delete Your Data via Facebook">
            <p>You can remove our app's access directly from Facebook by following these steps:</p>
            <ol className="list-decimal list-inside space-y-2 mt-2">
              <li>
                Log in to your{' '}
                <strong>Facebook account</strong> and go to{' '}
                <strong>Settings &amp; Privacy → Settings</strong>.
              </li>
              <li>
                Click on <strong>Apps and Websites</strong> in the left menu.
              </li>
              <li>
                Find <strong>Kalyani Consultancy</strong> in the list and click{' '}
                <strong>Remove</strong>.
              </li>
              <li>
                Tick the option <em>"Also delete all activity…"</em> and confirm removal.
              </li>
            </ol>
            <p className="mt-3">
              This revokes our app's access and removes the associated activity from Facebook's
              records.
            </p>
          </Section>

          <Section title="3. Request Deletion of Data Held by Us">
            <p>
              To request deletion of any personal data we hold on our own systems (CRM, email
              lists, etc.), please use the form below. We will process your request within{' '}
              <strong>30 days</strong> and send you a confirmation.
            </p>

            {submitted ? (
              <div className="mt-6 p-6 bg-green-50 border border-green-200 rounded-xl text-green-800 font-medium">
                Your email client should have opened with a pre-filled deletion request. If it did
                not open automatically, please email us directly at{' '}
                <a
                  href="mailto:kalyaniconsultancy18@gmail.com"
                  className="underline text-green-700"
                >
                  kalyaniconsultancy18@gmail.com
                </a>{' '}
                with the subject <em>"Data Deletion Request"</em>.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email address or Facebook-linked account
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors duration-200"
                >
                  Submit Deletion Request
                </button>
              </form>
            )}
          </Section>

          <Section title="4. What Happens After Your Request">
            <ul className="list-disc list-inside space-y-1">
              <li>We will verify your identity using the email or account details provided.</li>
              <li>All personal data linked to your account will be permanently deleted from our systems.</li>
              <li>You will receive a confirmation email within 30 days of your request.</li>
              <li>Some data may be retained where required by Indian financial regulations (IRDAI / SEBI / AMFI) or legal obligations, and you will be informed accordingly.</li>
            </ul>
          </Section>

          <Section title="5. Contact Us">
            <p>If you have questions about this process, reach out to us directly:</p>
            <ul className="mt-3 space-y-1">
              <li><strong>Kalyani Consultancy</strong></li>
              <li>5th Floor, Akash Ganga Complex, Basudevpur, Haldia, West Bengal 721602</li>
              <li>
                📞{' '}
                <a href="tel:+919476168969" className="text-blue-600 hover:underline">
                  +91 94761 68969
                </a>
              </li>
              <li>
                ✉️{' '}
                <a
                  href="mailto:kalyaniconsultancy18@gmail.com"
                  className="text-blue-600 hover:underline"
                >
                  kalyaniconsultancy18@gmail.com
                </a>
              </li>
            </ul>
          </Section>
        </motion.div>
      </section>
    </>
  );
}

export default DataDeletionPage;
